import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { renderContent } from '../renderContent';
import { apiClient } from '../../utils/apiClient';
import AuthImage from '../AuthImage';
import { generateSlug } from '../../utils/slugUtils';

const IFRAME_CONFIG = {
    ALLOWED_TAGS: ['iframe'],
    ALLOWED_ATTR: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'title'],
};

const ChatTimer = ({ timer, onClear }) => {
    const [timeLeft, setTimeLeft] = useState(Math.max(0, Math.floor((timer.endTime - Date.now()) / 1000)));
    const totalTime = timer.durationMinutes * 60;

    useEffect(() => {
        const intervalId = setInterval(() => {
            const newTimeLeft = Math.max(0, Math.floor((timer.endTime - Date.now()) / 1000));
            setTimeLeft(newTimeLeft);
            if (newTimeLeft <= 0) clearInterval(intervalId);
        }, 1000);
        return () => clearInterval(intervalId);
    }, [timer.endTime]);

    const minutes  = Math.floor(timeLeft / 60);
    const seconds  = timeLeft % 60;
    const progress = totalTime > 0 ? ((totalTime - timeLeft) / totalTime) * 100 : 100;

    return (
        <div className="group bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-white/[0.05] flex flex-col shrink-0 z-10 transition-all duration-300">
            <div className="overflow-hidden max-h-0 opacity-0 group-hover:max-h-20 group-hover:opacity-100 transition-all duration-300 ease-in-out">
                <div className="flex justify-between items-center font-bold text-cyan-600 dark:text-cyan-400 text-sm px-4 py-3">
                    <span className="flex items-center gap-2">⏱️ {timer.description}</span>
                    <div className="flex items-center gap-3">
                        <span className="font-mono bg-cyan-500/10 px-2 py-1 rounded-md">
                            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
                        </span>
                        <button onClick={onClear} className="text-gray-400 hover:text-red-500 transition-colors" title="Arrêter le minuteur">✖</button>
                    </div>
                </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-black/40 h-1.5 group-hover:h-2 transition-all duration-300">
                <div
                    className={`h-full transition-all duration-1000 ease-linear ${timeLeft <= 60 ? 'bg-red-500' : 'bg-gradient-to-r from-cyan-500 to-purple-500'}`}
                    style={{ width: `${progress}%` }}
                ></div>
            </div>
        </div>
    );
};

function AICoachChat({ user, onStartFlashcardSession, onStartQuizSession, sessionReport, cardsCount, quizzesCount, pageContext, isCoursePage }) {
    const [isOpen, setIsOpen]               = useState(false);
    const navigate                           = useNavigate();
    const [displayMessages, setDisplayMessages] = useState([]);
    const [inputValue, setInputValue]        = useState('');
    const [isLoading, setIsLoading]          = useState(false);
    const messagesEndRef                     = useRef(null);
    const [conversationId, setConversationId]   = useState(null);
    const [sessionStatus, setSessionStatus]  = useState('active'); // 'active' | 'closed'
    const [, setSessionSummary]   = useState(null);
    const [sessionTitle, setSessionTitle]    = useState(null);
    const [activeTimer, setActiveTimer]      = useState(null);
    const [isDaily, setIsDaily]              = useState(true);
    const [isContextualSession, setIsContextualSession] = useState(false); // Indique si la session a été créée automatiquement car on est sur un cours
    const [confirmEnd, setConfirmEnd]        = useState(false);
    const [chatView, setChatView]            = useState('chat'); // 'chat' | 'history' | 'history_detail'
    const [historyList, setHistoryList]      = useState([]);
    const [historyLoading, setHistoryLoading]   = useState(false);
    const [historyDetail, setHistoryDetail]  = useState(null); // { title, date, messages }
    
    // Réf pour empêcher le double appel dans useEffect (StrictMode)
    const isCreatingSessionRef = useRef(false);
    
    const [pendingUploadId, setPendingUploadId]         = useState(null);
    const [pendingImagePreview, setPendingImagePreview] = useState('');
    const [imageUploading, setImageUploading]           = useState(false);
    const imageInputRef                      = useRef(null);
    const lastProcessedReportRef             = useRef(null);
    const conversationIdRef                  = useRef(null);
    const sessionStatusRef                   = useRef('active');
    const forceNewSessionRef                 = useRef(false);
    const pendingAgendaEventIdRef            = useRef(null);

    // Synchroniser refs avec states
    useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
    useEffect(() => { sessionStatusRef.current  = sessionStatus; }, [sessionStatus]);

    // --- Timer récupéré depuis sessionStorage ---
    useEffect(() => {
        if (conversationId) {
            const stored = sessionStorage.getItem(`timer_${conversationId}`);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.endTime > Date.now()) setActiveTimer(parsed);
                else sessionStorage.removeItem(`timer_${conversationId}`);
            }
        }
    }, [conversationId]);

    const checkAndSetTimer = (aiMessages, convId) => {
        for (const msg of (aiMessages ?? [])) {
            const timerTool = (msg.tools ?? []).find(t => t.name === 'start_timer');
            if (timerTool) {
                const newTimer = {
                    endTime: Date.now() + timerTool.args.duration_minutes * 60000,
                    durationMinutes: timerTool.args.duration_minutes,
                    description: timerTool.args.description,
                };
                setActiveTimer(newTimer);
                if (convId) sessionStorage.setItem(`timer_${convId}`, JSON.stringify(newTimer));
                break;
            }
        }
    };

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    useEffect(() => { scrollToBottom(); }, [displayMessages, isOpen]);

    // Écoute de l'événement global pour ouvrir/fermer depuis la nav mobile
    useEffect(() => {
        const handleToggle = () => setIsOpen(prev => !prev);
        const handleOpen   = () => setIsOpen(true);
        window.addEventListener('toggle-axio-chat', handleToggle);
        window.addEventListener('open-axio-chat', handleOpen);
        return () => {
            window.removeEventListener('toggle-axio-chat', handleToggle);
            window.removeEventListener('open-axio-chat', handleOpen);
        };
    }, []);

    // Format unifié : utilisé pour get_conversations.php ET chat.php
    const processFormattedMessages = (messages) => {
        const processed = [];
        for (const msg of messages) {
            if (msg.role === 'user') {
                processed.push({ type: 'text', role: 'user', content: msg.text ?? '', upload_id: msg.upload_id ?? null });
            } else {
                if (msg.text?.trim()) {
                    processed.push({ type: 'text', role: 'assistant', content: msg.text });
                }
                for (const tool of (msg.tools ?? [])) {
                    if (tool.name === 'display_action_button') {
                        processed.push({ type: 'button', role: 'assistant', label: tool.args?.label, action: tool.args?.action, data: tool.args?.data });
                    } else if (tool.name === 'display_media') {
                        processed.push({ type: 'media', role: 'assistant', title: tool.args?.title, mediaType: tool.args?.type, content: tool.args?.content });
                    } else if (tool.name === 'suggest_quick_replies') {
                        processed.push({ type: 'quick_replies', role: 'assistant', options: tool.args?.options ?? [] });
                    }
                }
            }
        }
        return processed;
    };

    // --- Création d'une nouvelle session (utilisé au premier open ou après "Nouvelle session") ---
    const createNewSession = useCallback(async () => {
        if (isCreatingSessionRef.current) return;
        isCreatingSessionRef.current = true;
        
        const eventId = pendingAgendaEventIdRef.current;
        pendingAgendaEventIdRef.current = null;

        setIsLoading(true);
        try {
            const body = { 
                conversation_id: null,
                is_daily: forceNewSessionRef.current ? 0 : 1
            };
            if (eventId) body.agenda_event_id = eventId;

            const initResponse = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/chat.php`, {
                method: 'POST',
                body: JSON.stringify(body)
            });
            const initResult = await initResponse.json();
            if (initResult.success) {
                setConversationId(initResult.conversation_id);
                setSessionStatus('active');
                setDisplayMessages(processFormattedMessages(initResult.messages ?? []));
                checkAndSetTimer(initResult.messages, initResult.conversation_id);
            } else {
                setDisplayMessages([
                    { type: 'text', role: 'assistant', content: `Bonjour ${user.first_name || user.name} !` },
                    { type: 'text', role: 'assistant', content: `Je suis Axio. Comment puis-je t'aider ?` }
                ]);
            }
        } catch (error) {
            setDisplayMessages([
                { type: 'text', role: 'assistant', content: `Bonjour ${user.first_name || user.name} !` },
                { type: 'text', role: 'assistant', content: `Je suis Axio. Comment puis-je t'aider ?` }
            ]);
        } finally {
            setIsLoading(false);
            isCreatingSessionRef.current = false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.name, user.first_name]);

    // --- Chargement de la session au premier open ---
    useEffect(() => {
        if (isOpen && conversationId === null) {
            if (forceNewSessionRef.current) {
                forceNewSessionRef.current = false;
                createNewSession();
                return;
            }
            const loadConversation = async () => {
                try {
                    const loadResponse = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_conversations.php?daily=1`, { method: 'GET' });
                    const loadData     = await loadResponse.json();

                    if (loadData.success && loadData.conversation?.id) {
                        setConversationId(loadData.conversation.id);
                        setSessionStatus(loadData.conversation.status ?? 'active');
                        setSessionSummary(loadData.conversation.summary ?? null);
                        setIsDaily(!!loadData.conversation.is_daily);
                        setDisplayMessages(processFormattedMessages(loadData.messages));
                    } else {
                        await createNewSession();
                    }
                } catch (error) {
                    console.error("Erreur chargement session:", error);
                }
            };
            loadConversation();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, conversationId]);

    // --- Lancement d'une séance planifiée depuis l'agenda (via CustomEvent) ---
    useEffect(() => {
        const handler = (e) => {
            pendingAgendaEventIdRef.current = e.detail?.id ?? null;
            forceNewSessionRef.current = true;
            setIsOpen(true);
            setConversationId(null);
            setSessionStatus('active');
            setSessionSummary(null);
            setSessionTitle(null);
            setDisplayMessages([]);
            setActiveTimer(null);
            setIsContextualSession(false);
            setHistoryList([]);
        };
        window.addEventListener('start-agenda-session', handler);
        return () => window.removeEventListener('start-agenda-session', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Rapport de session flashcard/quiz ---
    useEffect(() => {
        if (sessionReport && sessionReport.data && sessionReport.t !== lastProcessedReportRef.current) {
            lastProcessedReportRef.current = sessionReport.t;
            const sendSessionReport = async () => {
                setIsOpen(true);
                setIsLoading(true);
                try {
                    const contentText = `CONTEXTE_APPLICATION_POUR_IA: L'étudiant vient de terminer une session de ${sessionReport.type}. Rapport :\n${typeof sessionReport.data === 'object' ? JSON.stringify(sessionReport.data) : sessionReport.data}\nRéagis brièvement et naturellement, analyse points forts/faibles, encourage-le.`;
                    const response = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/chat.php`, {
                        method: 'POST',
                        body: JSON.stringify({ message: contentText, role: 'app_to_model', conversation_id: conversationId })
                    });
                    const result = await response.json();
                    if (result.success) {
                        setConversationId(result.conversation_id);
                        setDisplayMessages(prev => [...prev, ...processFormattedMessages(result.messages ?? [])]);
                        checkAndSetTimer(result.messages, result.conversation_id);
                    }
                } catch (error) {
                    console.error("Erreur envoi rapport:", error);
                }
                setIsLoading(false);
            };
            sendSessionReport();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionReport, conversationId, cardsCount, quizzesCount]);

    // --- Upload image pour le chat ---
    const handleImageSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setPendingImagePreview(ev.target.result);
        reader.readAsDataURL(file);

        setImageUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/upload_image.php`, { method: 'POST', body: formData });
            const data = await res.json();
            if (data.success) setPendingUploadId(data.id);
            else { setPendingImagePreview(''); alert('Erreur lors de l\'upload de l\'image.'); }
        } catch { setPendingImagePreview(''); }
        setImageUploading(false);
        e.target.value = '';
    };

    // --- Envoi de message ---
    const handleSend = async (overrideText = null) => {
        const textToUse = typeof overrideText === 'string' ? overrideText : inputValue;
        if ((!textToUse.trim() && !pendingUploadId) || sessionStatus === 'closed') return;

        const imageForDisplay  = pendingImagePreview;
        const uploadIdToSend   = pendingUploadId;
        setDisplayMessages(prev => [...prev, { type: 'text', role: 'user', content: textToUse, image: imageForDisplay || null }]);
        const messageToSend = textToUse;
        if (typeof overrideText !== 'string') setInputValue('');
        setPendingUploadId(null);
        setPendingImagePreview('');
        setIsLoading(true);

        let currentTimerStatus = null;
        if (activeTimer && activeTimer.endTime > Date.now()) {
            const timeLeft = Math.floor((activeTimer.endTime - Date.now()) / 1000);
            currentTimerStatus = `Le minuteur actif "${activeTimer.description}" indique qu'il reste ${Math.floor(timeLeft / 60)} min ${timeLeft % 60} sec. Adapte tes réponses en conséquence.`;
        }

        try {
            const response = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/chat.php`, {
                method: 'POST',
                body: JSON.stringify({ message: messageToSend, conversation_id: conversationId, timer_status: currentTimerStatus, ...(pageContext && { page_context: pageContext }), ...(uploadIdToSend && { upload_id: uploadIdToSend }) })
            });
            const result = await response.json();

            if (result.session_closed) {
                setSessionStatus('closed');
                setDisplayMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Cette session est terminée." }]);
            } else if (result.success) {
                setConversationId(result.conversation_id);
                setDisplayMessages(prev => [...prev, ...processFormattedMessages(result.messages ?? [])]);
                checkAndSetTimer(result.messages, result.conversation_id);
            } else {
                setDisplayMessages(prev => [...prev, { type: 'text', role: 'assistant', content: result.message || "Une erreur est survenue." }]);
            }
        } catch (error) {
            setDisplayMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Erreur de connexion au serveur." }]);
        }
        setIsLoading(false);
    };

    // --- Terminer la session manuellement ---
    const handleEndSession = async () => {
        if (!conversationId || sessionStatus === 'closed') return;
        setIsLoading(true);
        try {
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/close_session.php`, {
                method: 'POST',
                body: JSON.stringify({ conversation_id: conversationId }),
            });
            const data = await res.json();
            if (data.daily_notification) {
                forceNewSessionRef.current = false;
                setConversationId(null); // Déclenche le useEffect pour charger la session du jour
                setSessionStatus('active');
                setSessionSummary(null);
                setSessionTitle(null);
                setDisplayMessages([]);
                setActiveTimer(null);
                setIsDaily(true);
            } else {
                setSessionStatus('closed');
                setSessionSummary(data.summary ?? null);
                if (data.title) setSessionTitle(data.title);
            }
        } catch (e) {
            console.error("Erreur fermeture session:", e);
        }
        setConfirmEnd(false);
        setIsLoading(false);
    };

    // --- Basculement contextuel du chat (Chat Caméléon) ---
    const prevIsCoursePage = useRef(isCoursePage);
    useEffect(() => {
        if (prevIsCoursePage.current !== isCoursePage) {
            if (isCoursePage) {
                // L'élève entre sur une page de cours : s'il était sur la session du jour, on force une nouvelle session
                if (isDaily && conversationId) {
                    forceNewSessionRef.current = true;
                    setConversationId(null);
                    setSessionStatus('active');
                    setSessionSummary(null);
                    setSessionTitle(null);
                    setDisplayMessages([]);
                    setActiveTimer(null);
                    setIsDaily(false);
                    setIsContextualSession(true);
                }
            } else {
                // L'élève sort d'une page de cours : si la session contextuelle était active, on la clôture auto
                if (isContextualSession && conversationId && sessionStatus === 'active') {
                    handleEndSession();
                }
            }
            prevIsCoursePage.current = isCoursePage;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCoursePage, isDaily, conversationId, sessionStatus, isContextualSession]);

    // --- Historique ---
    const openHistory = async () => {
        setChatView('history');
        if (historyList.length > 0) return;
        setHistoryLoading(true);
        try {
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_conversations.php`, { method: 'GET' });
            const data = await res.json();
            if (data.success) setHistoryList(data.conversations);
        } catch {}
        setHistoryLoading(false);
    };

    const openHistoryDetail = async (convId, title, date, isActive) => {
        if (isActive) { setChatView('chat'); return; }
        setChatView('history_detail');
        setHistoryDetail({ title, date, messages: null });
        try {
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_conversations.php?id=${convId}`, { method: 'GET' });
            const data = await res.json();
            if (data.success) {
                setHistoryDetail({ title, date, messages: processFormattedMessages(data.messages) });
            }
        } catch {}
    };

    // --- Nouvelle session ---
    const handleNewSession = () => {
        forceNewSessionRef.current = true;
        setConversationId(null);
        setSessionStatus('active');
        setSessionSummary(null);
        setSessionTitle(null);
        setDisplayMessages([]);
        setActiveTimer(null);
        setIsDaily(false);
        setIsContextualSession(false);
        setHistoryList([]);
    };

    // --- Boutons d'action de l'IA ---
    const handleActionButtonClick = async (action, data) => {
        if (action === 'EndSession') {
            await handleEndSession();
            return;
        }
        if (action === 'FlashcardSession') {
            setIsLoading(true);
            try {
                const subjectParam = data?.subject_name ? `?subject=${encodeURIComponent(data.subject_name)}` : '';
                const response = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_flashcard_session.php${subjectParam}`, { method: 'GET' });
                const result   = await response.json();
                if (result.success && result.cards?.length > 0) {
                    // Lancer un timer de 5 min pour borner le rappel actif
                    const timerData = {
                        endTime: Date.now() + 5 * 60000,
                        durationMinutes: 5,
                        description: data?.subject_name ? `Rappel actif — ${data.subject_name}` : 'Rappel actif',
                    };
                    setActiveTimer(timerData);
                    if (conversationId) sessionStorage.setItem(`timer_${conversationId}`, JSON.stringify(timerData));
                    setIsOpen(false);
                    if (onStartFlashcardSession) onStartFlashcardSession(result.cards);
                } else {
                    setDisplayMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Pas de flashcards à réviser pour le moment 👍" }]);
                }
            } catch (error) {
                setDisplayMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Erreur lors du chargement des flashcards." }]);
            }
            setIsLoading(false);
        } else if (action === 'QuizSession') {
            setIsLoading(true);
            try {
                const subjectParam = data?.subject_name ? `?subject=${encodeURIComponent(data.subject_name)}` : '';
                const response = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_quiz_session.php${subjectParam}`, { method: 'GET' });
                const result   = await response.json();
                if (result.success && result.quizzes?.length > 0) {
                    // Lancer un timer de 5 min pour borner le rappel actif
                    const timerData = {
                        endTime: Date.now() + 5 * 60000,
                        durationMinutes: 5,
                        description: data?.subject_name ? `Rappel actif — ${data.subject_name}` : 'Rappel actif',
                    };
                    setActiveTimer(timerData);
                    if (conversationId) sessionStorage.setItem(`timer_${conversationId}`, JSON.stringify(timerData));
                    setIsOpen(false);
                    if (onStartQuizSession) onStartQuizSession(result.quizzes);
                } else {
                    setDisplayMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Pas de Quiz à réviser pour le moment 👍" }]);
                }
            } catch (error) {
                setDisplayMessages(prev => [...prev, { type: 'text', role: 'assistant', content: "Erreur lors du chargement des Quiz." }]);
            }
            setIsLoading(false);
        } else if (action === 'ViewCourse') {
            if (data) {
                const anchor = data.section_id ? `#section-${data.section_id}` : (data.notion_id ? `#notion-${data.notion_id}` : '');
                setIsOpen(false);
                if (data.subject_name && data.chapter_name) {
                    navigate(`/cours/${generateSlug(data.subject_name)}/${generateSlug(data.chapter_name)}${anchor}`);
                } else if (data.chapter_id) {
                    navigate(`/cours?chapter_id=${data.chapter_id}${anchor}`);
                }
            }
        }
    };

    const isClosed = sessionStatus === 'closed';

    return (
        <div className={`fixed z-[150] flex flex-col items-end pointer-events-none ${isOpen ? 'inset-0' : 'bottom-0 md:bottom-6 right-0 md:right-6 w-full md:w-auto'}`}>
            {isOpen && (
                <div className="pointer-events-auto w-full h-full bg-white/95 dark:bg-gray-950/95 backdrop-blur-3xl flex flex-col overflow-hidden animate-fade-in md:w-[400px] md:h-[550px] max-h-none md:max-h-[75vh] md:rounded-3xl shadow-[0_-10px_50px_rgba(6,182,212,0.2)] md:shadow-[0_0_50px_rgba(6,182,212,0.2)] md:border border-gray-200/50 dark:border-white/[0.1] mb-0 md:mb-6">

                    {/* En-tête */}
                    <div className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 p-4 border-b border-gray-200 dark:border-white/[0.05] flex justify-between items-center shadow-md">
                        <div className="flex items-center gap-2">
                            {chatView !== 'chat' && (
                                <button
                                    onClick={() => setChatView('chat')}
                                    title="Retour au chat"
                                    className="text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors shrink-0"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6"/>
                                    </svg>
                                </button>
                            )}
                            <div>
                                <h3 className="text-gray-800 dark:text-white font-bold text-sm tracking-wide">Axio</h3>
                                {chatView === 'chat' && (
                                    <p className={`text-xs font-semibold flex items-center gap-1 ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-cyan-700 dark:text-cyan-300'}`}>
                                        <span className={`w-2 h-2 rounded-full ${isClosed ? 'bg-gray-400' : 'bg-cyan-500 dark:bg-cyan-400 animate-pulse'}`}></span>
                                        {isClosed ? 'Session terminée' : 'En ligne'}
                                    </p>
                                )}
                                {chatView === 'history' && <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold">Historique</p>}
                                {chatView === 'history_detail' && <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold">Session passée</p>}
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {chatView === 'chat' && (
                                <button
                                    onClick={openHistory}
                                    title="Historique des sessions"
                                    className="text-gray-400 hover:text-gray-700 dark:hover:text-white w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                </button>
                            )}
                            {chatView === 'chat' && !isClosed && !isDaily && (
                                <button
                                    onClick={() => setConfirmEnd(v => !v)}
                                    title="Terminer la session"
                                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-500/10 transition-colors"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                title="Réduire"
                                className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors"
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <line x1="5" y1="12" x2="19" y2="12"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Confirmation terminer session */}
                    {confirmEnd && (
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-500/5 border-b border-red-500/20 text-xs">
                            <span className="text-gray-600 dark:text-gray-300 font-medium">Terminer cette session ?</span>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleEndSession}
                                    disabled={isLoading}
                                    className="font-bold text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
                                >
                                    Terminer
                                </button>
                                <button
                                    onClick={() => setConfirmEnd(false)}
                                    className="font-bold text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Timer */}
                    {activeTimer && activeTimer.endTime > Date.now() && (
                        <ChatTimer
                            timer={activeTimer}
                            onClear={() => {
                                setActiveTimer(null);
                                if (conversationId) sessionStorage.removeItem(`timer_${conversationId}`);
                            }}
                        />
                    )}

                    {/* Vue Historique — liste */}
                    {chatView === 'history' && (
                        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
                            {historyLoading && <p className="text-center text-gray-400 text-sm py-8">Chargement...</p>}
                            {!historyLoading && historyList.length === 0 && (
                                <p className="text-center text-gray-400 text-sm py-8">Aucune session passée.</p>
                            )}
                            {historyList.map(conv => {
                                const d        = new Date(conv.created_at);
                                const isActive = conv.status === 'active';
                                return (
                                    <button
                                        key={conv.id}
                                        onClick={() => openHistoryDetail(conv.id, conv.title, d, conv.status === 'active')}
                                        className={`w-full text-left p-3 rounded-xl border transition-all group ${isActive ? 'bg-cyan-500/5 dark:bg-cyan-500/[0.06] border-cyan-500/30 hover:border-cyan-500/50 hover:bg-cyan-500/10' : 'bg-gray-100 dark:bg-white/[0.04] border-gray-200 dark:border-white/[0.06] hover:border-cyan-500/30 hover:bg-gray-200 dark:hover:bg-white/[0.08]'}`}
                                    >
                                        <div className="flex items-center gap-2 mb-0.5">
                                            {isActive && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-cyan-600 dark:text-cyan-400 shrink-0">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse"/>
                                                    {conv.is_daily ? 'Session du jour' : 'En cours'}
                                                </span>
                                            )}
                                            <p className="text-sm font-semibold text-gray-800 dark:text-white truncate group-hover:text-cyan-700 dark:group-hover:text-cyan-400 transition-colors">
                                                {conv.title ?? 'Session du jour'}
                                            </p>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} · {d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Vue Historique — détail session */}
                    {chatView === 'history_detail' && (
                        <div className="flex-1 overflow-y-auto p-4 flex flex-col">
                            {historyDetail?.title && (
                                <div className="mb-4 pb-3 border-b border-gray-200 dark:border-white/[0.05]">
                                    <p className="text-xs font-bold text-gray-800 dark:text-white">{historyDetail.title}</p>
                                    {historyDetail.date && (
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                            {historyDetail.date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                                        </p>
                                    )}
                                </div>
                            )}
                            {!historyDetail?.messages && (
                                <p className="text-center text-gray-400 text-sm py-8">Chargement...</p>
                            )}
                            {historyDetail?.messages?.map((msg, idx) => {
                                const showName = idx === 0 || historyDetail.messages[idx - 1].role !== msg.role;
                                return (
                                    <div key={idx} className={`flex flex-col w-full ${msg.role === 'user' ? 'items-end' : 'items-start'} ${showName && idx !== 0 ? 'mt-5' : 'mt-1.5'}`}>
                                        {showName && (
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1 px-1 font-bold">
                                                {msg.role === 'user' ? 'Toi' : 'Axio'}
                                            </span>
                                        )}
                                        {msg.type === 'text' && (() => {
                                            const contentToRender = (msg.content || '').replace(/\[QUICK_REPLIES:\s*(.+?)\]/, '').trim();
                                            return (
                                                <div className={`p-3 rounded-2xl text-xs leading-relaxed max-w-[90%] shadow-sm ${msg.role === 'user' ? 'bg-cyan-500/15 border border-cyan-500/20 text-cyan-900 dark:text-cyan-50 rounded-tr-sm' : 'bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.05] text-gray-700 dark:text-gray-300 rounded-tl-sm'}`}>
                                                    {renderContent(contentToRender)}
                                                </div>
                                            );
                                        })()}
                                        {msg.type === 'button' && (
                                            <div className="px-4 py-2 mt-1 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] text-gray-400 opacity-60">
                                                {msg.label}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Corps (Messages) + Footer — chat actif */}
                    {chatView === 'chat' && (
                    <>
                    <div className="flex-1 overflow-y-auto p-4 flex flex-col">
                        {displayMessages.map((msg, idx) => {
                            const showName = idx === 0 || displayMessages[idx - 1].role !== msg.role;
                            return (
                                <div key={idx} className={`flex flex-col w-full ${msg.role === 'user' ? 'items-end' : 'items-start'} ${showName && idx !== 0 ? 'mt-5' : 'mt-1.5'}`}>
                                    {showName && (
                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1 px-1 font-bold">{msg.role === 'user' ? 'Vous' : 'Axio'}</span>
                                    )}
                                    {msg.type === 'text' && (() => {
                                        let contentToRender = msg.content || '';
                                        let quickReplies = [];
                                        const qrMatch = contentToRender.match(/\[QUICK_REPLIES:\s*(.+?)\]/);
                                        if (qrMatch) {
                                            quickReplies = qrMatch[1].split('|').map(s => s.trim());
                                            contentToRender = contentToRender.replace(qrMatch[0], '').trim();
                                        }
                                        const isLastMessage = idx === displayMessages.length - 1;

                                        return (
                                            <div className="flex flex-col gap-2 max-w-[90%] w-full">
                                                <div className={`p-4 rounded-2xl text-sm leading-relaxed shadow-sm w-fit ${msg.role === 'user' ? 'bg-cyan-500/20 border border-cyan-500/30 text-cyan-900 dark:text-cyan-50 rounded-tr-sm self-end' : 'bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.05] text-gray-800 dark:text-gray-200 rounded-tl-sm self-start'}`}>
                                                    {msg.image && <img src={msg.image} alt="" className="max-w-full rounded-xl mb-2 max-h-48 object-contain" />}
                                                    {msg.upload_id && <AuthImage uploadId={msg.upload_id} className="max-w-full rounded-xl mb-2 max-h-48 object-contain" />}
                                                    {contentToRender && renderContent(contentToRender)}
                                                </div>
                                                {quickReplies.length > 0 && isLastMessage && !isClosed && (
                                                    <div className={`flex flex-wrap gap-2 mt-1 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                                        {quickReplies.map((qr, qrIdx) => (
                                                            <button 
                                                                key={qrIdx}
                                                                onClick={() => handleSend(qr)}
                                                                className="px-4 py-2 bg-white dark:bg-gray-800 border border-cyan-200 dark:border-cyan-500/30 hover:border-cyan-400 dark:hover:border-cyan-400 text-cyan-700 dark:text-cyan-300 rounded-full text-xs font-semibold shadow-sm transition-all hover:-translate-y-0.5 active:translate-y-0"
                                                            >
                                                                {qr}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    {msg.type === 'quick_replies' && idx === displayMessages.length - 1 && !isClosed && (
                                        <div className={`flex flex-wrap gap-2 mt-2 w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                            {msg.options.map((qr, qrIdx) => (
                                                <button 
                                                    key={qrIdx}
                                                    onClick={() => handleSend(qr)}
                                                    className="px-4 py-2 bg-white dark:bg-gray-800 border border-cyan-200 dark:border-cyan-500/30 hover:border-cyan-400 dark:hover:border-cyan-400 text-cyan-700 dark:text-cyan-300 rounded-full text-xs font-semibold shadow-sm transition-all hover:-translate-y-0.5 active:translate-y-0"
                                                >
                                                    {qr}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {msg.type === 'button' && (() => {
                                        const isDisabled = (msg.action === 'FlashcardSession' && cardsCount === 0) ||
                                                           (msg.action === 'QuizSession' && quizzesCount === 0) ||
                                                           (msg.action === 'ViewCourse' && (!msg.data || (!msg.data.chapter_id && !msg.data.chapter_name)));
                                        const isEnd = msg.action === 'EndSession';
                                        return (
                                            <button
                                                onClick={() => handleActionButtonClick(msg.action, msg.data)}
                                                disabled={isDisabled || isClosed}
                                                className={`px-6 py-3 rounded-xl font-bold mt-2 transition-all duration-300 ${isDisabled || isClosed ? 'text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-white/[0.05] border border-gray-300 dark:border-white/[0.05] cursor-not-allowed opacity-70' : isEnd ? 'text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20' : 'text-fuchsia-700 dark:text-white bg-fuchsia-500/10 border border-fuchsia-500/50 hover:bg-fuchsia-500/20 hover:shadow-[0_0_30px_rgba(217,70,239,0.3)]'}`}
                                            >
                                                {msg.label}
                                            </button>
                                        );
                                    })()}
                                    {msg.type === 'media' && (
                                        <div className="p-4 mt-2 max-w-[90%] w-[320px] rounded-2xl text-sm leading-relaxed shadow-sm bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.05] text-gray-800 dark:text-gray-200 rounded-tl-sm">
                                            <h4 className="font-bold mb-3 text-cyan-600 dark:text-cyan-400 flex items-center gap-2">
                                                {msg.mediaType === 'video' ? '📺' : msg.mediaType === 'podcast' ? '🎧' : '🖼️'} {msg.title}
                                            </h4>
                                            {msg.mediaType === 'video' && (
                                                <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
                                                    {msg.content.includes('<iframe') ? (
                                                        <div className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content, IFRAME_CONFIG) }} />
                                                    ) : (
                                                        <video controls className="w-full h-full object-cover">
                                                            <source src={msg.content} />
                                                        </video>
                                                    )}
                                                </div>
                                            )}
                                            {msg.mediaType === 'podcast' && (
                                                <audio controls className="w-full focus:outline-none rounded-full h-10 mt-2">
                                                    <source src={msg.content} />
                                                </audio>
                                            )}
                                            {msg.mediaType === 'image' && (
                                                <img src={msg.content} alt={msg.title} className="w-full h-auto rounded-xl shadow-sm" />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {isLoading && (
                            <div className={`flex flex-col w-full items-start ${displayMessages.length > 0 && displayMessages[displayMessages.length - 1].role === 'user' ? 'mt-5' : 'mt-1.5'}`}>
                                {(displayMessages.length === 0 || displayMessages[displayMessages.length - 1].role !== 'assistant') && (
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1 px-1 font-bold">Axio</span>
                                )}
                                <div className="p-4 rounded-2xl bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.05] text-gray-400 rounded-tl-sm text-sm flex items-center gap-2 shadow-sm">
                                    <span className="animate-bounce">●</span><span className="animate-bounce" style={{ animationDelay: "0.2s" }}>●</span><span className="animate-bounce" style={{ animationDelay: "0.4s" }}>●</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Bannière session fermée */}
                    {isClosed && (
                        <div className="mx-4 mb-3 p-3 rounded-xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-xs text-gray-500 dark:text-gray-400">
                            <p className="font-bold text-gray-600 dark:text-gray-300">{sessionTitle ?? 'Session terminée'}</p>
                            <p className="mt-0.5">Démarre une nouvelle session pour continuer.</p>
                        </div>
                    )}

                    {/* Zone de saisie ou bouton Nouvelle session */}
                    <div
                        className="p-4 border-t border-gray-200 dark:border-white/[0.05] bg-gray-100 dark:bg-black/40"
                        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
                    >
                        {isClosed ? (
                            <button
                                onClick={handleNewSession}
                                className="w-full py-3 rounded-xl font-bold text-sm bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 transition-all"
                            >
                                Nouvelle session
                            </button>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {pendingImagePreview && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
                                        <img src={pendingImagePreview} alt="" className="h-10 w-10 object-cover rounded-lg shrink-0" />
                                        <span className="text-xs text-cyan-600 dark:text-cyan-400 flex-1 truncate">
                                            {imageUploading ? 'Upload en cours...' : 'Photo jointe'}
                                        </span>
                                        <button onClick={() => { setPendingUploadId(null); setPendingImagePreview(''); }} className="text-gray-400 hover:text-red-500 transition-colors text-xs font-bold">✕</button>
                                    </div>
                                )}
                                <div className="flex gap-2 items-end">
                                    <button
                                        onClick={() => imageInputRef.current?.click()}
                                        disabled={imageUploading}
                                        title="Joindre une photo"
                                        className="shrink-0 text-gray-400 hover:text-cyan-500 dark:hover:text-cyan-400 disabled:opacity-40 transition-colors p-2"
                                    >
                                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                                        </svg>
                                    </button>
                                    <textarea
                                        value={inputValue}
                                        onChange={(e) => {
                                            setInputValue(e.target.value);
                                            e.target.style.height = 'auto';
                                            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                e.target.style.height = 'auto';
                                                handleSend();
                                            }
                                        }}
                                        placeholder="Pose ta question… (Maj+Entrée pour sauter une ligne)"
                                        rows={1}
                                        className="flex-1 bg-white dark:bg-white/[0.05] border border-gray-300 dark:border-white/[0.1] rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-white focus:outline-none focus:border-cyan-500 transition-colors resize-none overflow-hidden leading-5"
                                    />
                                    <button
                                        onClick={handleSend}
                                        disabled={isLoading || imageUploading || (!inputValue.trim() && !pendingUploadId)}
                                        className="shrink-0 bg-cyan-500/20 text-cyan-700 dark:text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 disabled:opacity-50 px-5 py-3 rounded-xl transition-all font-bold shadow-[0_0_15px_rgba(6,182,212,0.2)]"
                                    >
                                        ➤
                                    </button>
                                </div>
                                <input type="file" ref={imageInputRef} accept="image/*" className="hidden" onChange={handleImageSelect} />
                            </div>
                        )}
                    </div>
                    </>
                    )}
                </div>
            )}

            {/* Bulle flottante bureau */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`pointer-events-auto hidden md:flex w-16 h-16 rounded-full shadow-[0_0_30px_rgba(6,182,212,0.4)] items-center justify-center text-white text-3xl transition-all transform hover:scale-110 z-50 ${isOpen ? 'bg-gray-800 border border-white/[0.1]' : 'bg-gradient-to-r from-cyan-500 to-purple-600 hover:shadow-[0_0_40px_rgba(168,85,247,0.6)]'}`}
            >
                {isOpen ? (
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                ) : (
                    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                )}
            </button>
        </div>
    );
}

export default AICoachChat;
